import { useEffect } from 'react'
import type React from 'react'
import type { ComponentPropsWithoutRef } from 'react'
import { useT } from '@transifex/react'
import { detectCreditCard } from '@vend/credit-card-detector'
import { noWidow } from '@vend/utilities'
import { Textarea } from '@lightspeed/unified-components-helios-theme/react'

export type NoteError = 'CC_DETECTED' | 'TOO_LONG'

const NOTE_THRESHOLD = 50

type TextareaProps = ComponentPropsWithoutRef<typeof Textarea>

// We use Pick<TextareaProps, ...> instead of `extends TextareaProps` because
// TypeScript 5 cannot fully resolve the complex generic type exported by
// unified-components when used directly in an `extends` clause, causing
// passed-through props like `placeholder` to be unrecognized. Pick forces
// the type to be evaluated into a concrete object type before extending.
export interface NoteFieldProps
  extends Partial<
    Pick<
      TextareaProps,
      'placeholder' | 'name' | 'onChange' | 'onBlur' | 'value' | 'disabled'
    >
  > {
  charLimit: number
  autoFocus?: boolean
  label?: React.ReactNode
  descriptionSlot?: React.ReactNode
  onErrorsChange?: (errors: NoteError[]) => void
  onValidChange?: (valid: boolean) => void
}

export interface NoteFieldModel {
  isValid: boolean
  errors: string[]
  hintMessage: string | false
}

export const useNoteFieldModel = (props: NoteFieldProps): NoteFieldModel => {
  const t = useT()
  const value = props.value ?? ''
  const hasCreditCard = detectCreditCard(value)
  const noteTooLong = value.length > props.charLimit
  const shouldShowCount = value.length >= props.charLimit - NOTE_THRESHOLD
  const remainingChars = props.charLimit - value.length
  const { onErrorsChange, onValidChange } = props
  const errorMap: Partial<Record<NoteError, string>> = {}
  let isValid = true

  if (noteTooLong) {
    isValid = false
    errorMap.TOO_LONG = t(
      '{chars, plural, one {{chars} character over the limit} other {{chars} characters over the limit}}',
      {
        _context: 'Input character limit message',
        _comment:
          '"chars" variable is replaced with the number of characters over the limit. The "one" plural form is used for 1; "other" is used for more than 1 in English.',
        chars: Math.abs(remainingChars),
      }
    )
  }

  if (hasCreditCard) {
    isValid = false
    errorMap.CC_DETECTED = noWidow(
      t(
        'You’ve entered information that looks like credit card details, which could be highly sensitive. Please remove them to continue.',
        { _context: 'Error message' }
      )
    )
  }

  const hintMessage =
    shouldShowCount &&
    !noteTooLong &&
    t(
      '{chars, plural, one {{chars} remaining character} other {{chars} remaining characters}}',
      {
        _context: 'Input character limit message',
        _comment:
          '"chars" variable is replaced with the number of characters remaining. The "one" plural form is used for 1; "other" is used for 0 or more than 1 in English.',
        chars: remainingChars,
      }
    )

  useEffect(() => {
    if (!onErrorsChange) return
    onErrorsChange(Object.keys(errorMap) as NoteError[])
    // errorMap is not included in the dependency array because it is a new object on every render, which would cause an infinite loop of updates.
    // The values of errorMap are derived from hasCreditCard and noteTooLong, which are included in the dependency array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCreditCard, noteTooLong, onErrorsChange])

  useEffect(() => {
    if (!onValidChange) return
    onValidChange(isValid)
  }, [isValid, onValidChange])

  return {
    isValid,
    errors: Object.values(errorMap),
    hintMessage,
  }
}
